import { v4 as uuidv4 } from "uuid";

export const TARGET_CAMPAIGN_ID = "0PljxvChIWg8SHRjA36w";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntryOptions {
  event: string;
  level?: LogLevel;
  correlationId?: string;
  campaignId?: string;
  details?: Record<string, unknown>;
}

interface CacheDecisionParams {
  cacheName: string;
  correlationId: string;
  campaignId?: string;
  forceRefresh?: boolean;
  cacheEntry?: { timestamp: number };
  ttlMs: number;
}

interface CacheDecisionResult {
  useCache: boolean;
}

const getTimestamp = () => new Date().toISOString();

const ensureCorrelationId = (candidate?: string) => candidate ?? uuidv4();

const serializeError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  if (typeof error === "object" && error !== null) {
    return { ...error };
  }

  return { message: "unknown error" };
};

const logToConsole = (level: LogLevel, entry: Record<string, unknown>) => {
  const method: "error" | "warn" | "debug" | "info" =
    level === "error"
      ? "error"
      : level === "warn"
      ? "warn"
      : level === "debug"
      ? "debug"
      : "info";

  const logger = console[method] ?? console.log;
  //logger(JSON.stringify(entry));
};

export const logEngagementEvent = (options: LogEntryOptions) => {
  const level: LogLevel = options.level ?? "info";
  const correlationId = ensureCorrelationId(options.correlationId);
  const entry = {
    level,
    event: options.event,
    timestamp: getTimestamp(),
    correlationId,
    campaignId: options.campaignId,
    targetCampaignId: TARGET_CAMPAIGN_ID,
    metadata: options.details ?? {},
  };

  logToConsole(level, entry);
  return correlationId;
};

export const logCacheDecision = (
  params: CacheDecisionParams
): CacheDecisionResult => {
  const {
    cacheName,
    correlationId,
    campaignId,
    forceRefresh = false,
    cacheEntry,
    ttlMs,
  } = params;
  const entryAgeMs = cacheEntry ? Date.now() - cacheEntry.timestamp : undefined;
  const baseDetails: Record<string, unknown> = {
    cacheName,
    ttlMs,
    forceRefresh,
    cacheAgeMs: entryAgeMs,
  };

  if (forceRefresh) {
    logEngagementEvent({
      event: `${cacheName}.forceRefresh`,
      level: "info",
      correlationId,
      campaignId,
      details: baseDetails,
    });
    return { useCache: false };
  }

  if (!cacheEntry) {
    logEngagementEvent({
      event: `${cacheName}.miss`,
      level: "debug",
      correlationId,
      campaignId,
      details: baseDetails,
    });
    return { useCache: false };
  }

  const cacheValid = entryAgeMs !== undefined && entryAgeMs < ttlMs;

  if (cacheValid) {
    logEngagementEvent({
      event: `${cacheName}.hit`,
      level: "debug",
      correlationId,
      campaignId,
      details: baseDetails,
    });
    return { useCache: true };
  }

  logEngagementEvent({
    event: `${cacheName}.expired`,
    level: "info",
    correlationId,
    campaignId,
    details: {
      ...baseDetails,
      cacheAgeMs: entryAgeMs,
    },
  });

  return { useCache: false };
};

export const instrumentAsyncCall = async <T>(params: {
  operation: string;
  correlationId: string;
  campaignId?: string;
  details?: Record<string, unknown>;
  run: () => Promise<T>;
}): Promise<T> => {
  const { operation, correlationId, campaignId, details = {} } = params;
  const start = Date.now();
  logEngagementEvent({
    event: `${operation}.start`,
    correlationId,
    campaignId,
    details,
  });

  try {
    const result = await params.run();
    logEngagementEvent({
      event: `${operation}.success`,
      correlationId,
      campaignId,
      details: {
        ...details,
        durationMs: Date.now() - start,
      },
    });
    return result;
  } catch (error) {
    logEngagementEvent({
      level: "error",
      event: `${operation}.error`,
      correlationId,
      campaignId,
      details: {
        ...details,
        durationMs: Date.now() - start,
        error: serializeError(error),
      },
    });
    throw error;
  }
};

export const logRawPayloadIfTarget = (options: {
  correlationId: string;
  campaignId?: string;
  operation: string;
  payload: unknown;
}) => {
  if (options.campaignId !== TARGET_CAMPAIGN_ID) {
    return;
  }

  logEngagementEvent({
    event: `${options.operation}.rawPayload`,
    level: "debug",
    correlationId: options.correlationId,
    campaignId: options.campaignId,
    details: {
      rawPayload: options.payload,
    },
  });
};

export const resolveCorrelationId = ensureCorrelationId;
